import React from 'react'

import { Table } from '@/components/tailwind-ui/components/Table'
import { Badge } from '@/components/tailwind-ui/components/Badge'
import getCreditCardIcon from '@/utils/getCreditCardIcon'

function ChargesTable({ charges = [], loading = false, titleSize = 'lg' }) {
  // Helper function to format currency
  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100) // Stripe amounts are in cents
  }

  // Helper function to format date
  const formatDate = timestamp => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Helper function to get card display text
  const getCardDisplay = paymentMethodDetails => {
    if (!paymentMethodDetails?.card) return 'N/A'

    const { brand, last4 } = paymentMethodDetails.card

    return `**** **** **** ${last4} (${brand?.toUpperCase()})`
  }

  // Helper function to get status badge
  const getStatusBadge = (status, disputed = false) => {
    if (disputed) {
      return <Badge variant="destructive">Disputed</Badge>
    }

    switch (status) {
      case 'succeeded':
        return <Badge variant="success">Succeeded</Badge>
      case 'pending':
        return <Badge variant="warning">Pending</Badge>
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge variant="gray">{status}</Badge>
    }
  }

  // Table columns configuration
  const columns = [
    {
      key: 'amount',
      header: 'Amount',
      accessor: 'amount',
      render: (value, row) => <div className="font-medium">{formatCurrency(value, row.currency)}</div>,
    },
    {
      key: 'date',
      header: 'Date',
      accessor: 'created',
      render: value => <div className="text-sm text-gray-900">{formatDate(value)}</div>,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      render: (value, row) => getStatusBadge(value, row.disputed),
    },
    {
      key: 'card',
      header: 'Payment Method',
      accessor: 'payment_method_details',
      render: (value, row) => {
        if (!value?.card) return <span className="text-gray-400">—</span>

        const { brand, last4 } = value.card

        return (
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">{getCreditCardIcon(brand)}</div>
            <span className="text-sm font-medium text-gray-900">•••• {last4}</span>
          </div>
        )
      },
    },
    {
      key: 'credits',
      header: 'Credits Purchased',
      accessor: 'metadata',
      render: metadata => {
        const creditAmount = metadata?.creditAmount

        return creditAmount ? (
          <Badge variant="info">{creditAmount} credits</Badge>
        ) : (
          <span className="text-gray-400">—</span>
        )
      },
    },
    {
      key: 'description',
      header: 'Description',
      accessor: 'description',
      render: (value, row) => {
        // If no description, try to use metadata type or show default
        const description = value || row.metadata?.type || 'Payment'

        return (
          <div className="text-sm text-gray-900">
            {description.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`text-${titleSize} font-medium text-gray-900`}>Payment History</h3>
        {charges.length > 0 && (
          <span className="text-sm text-gray-500">
            {charges.length} charge{charges.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <Table
        className="min-w-full"
        columns={columns}
        data={charges}
        emptyMessage="No charges found"
        loading={loading}
        searchable={false}
      />
    </div>
  )
}

export default ChargesTable
